const VALID_RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
const VALID_SEARCH_SUITS = ['x', 's', 'h', 'c', 'd'];
const VALID_REAL_SUITS = ['s', 'h', 'c', 'd'];
const STRAIGHT_RANKS = [
  ['A', 'K', 'Q', 'J', 'T'],
  ['K', 'Q', 'J', 'T', '9'],
  ['Q', 'J', 'T', '9', '8'],
  ['J', 'T', '9', '8', '7'],
  ['T', '9', '8', '7', '6'],
  ['9', '8', '7', '6', '5'],
  ['8', '7', '6', '5', '4'],
  ['7', '6', '5', '4', '3'],
  ['6', '5', '4', '3', '2'],
  ['A', '5', '4', '3', '2'],
];

export const getPostflopMaxCards = (isPlo5 = false) => (isPlo5 ? 5 : 4);

export const getPostflopMaxSearchLength = (isPlo5 = false) => getPostflopMaxCards(isPlo5) * 2;

export const stripHandToRanks = (hand = '') => hand
  .split('')
  .filter((char) => /[AKQJT2-9]/i.test(char))
  .map((char) => char.toUpperCase())
  .join('');

export const handHasSuitNotation = (hand = '') => VALID_SEARCH_SUITS.includes(hand.split('')[1]?.toLowerCase());

export const splitPostflopHand = (hand = '') => {
  if (!hand) return [];

  if (handHasSuitNotation(hand)) {
    return hand.match(/.{1,2}/g) || [];
  }

  return hand.match(/.{1}/g) || [];
};

export const getPostflopBoardSuit = (board = '', fallback = 'two-tone') => {
  const cards = splitPostflopHand(board);
  if (cards.length !== 3
    || cards.some((card) => !VALID_REAL_SUITS.includes(card[1]?.toLowerCase()))) return fallback;
  const count = new Set(cards.map((card) => card[1].toLowerCase())).size;
  if (count === 1) return 'mono';
  return count === 2 ? 'two-tone' : 'rainbow';
};

export const shouldRequestPostflopSimilar = (hand = '', isPlo5 = false) => (
  hand.trim().length > 0 && (!isPlo5 || splitPostflopHand(hand).length === 5)
);

export const normalizePostflopSimilarHand = (
  hand = '',
  isPlo5 = false,
  isStudyMode = true,
) => {
  const normalizedHand = isStudyMode ? hand.replaceAll('x', '') : stripHandToRanks(hand);
  return isPlo5 ? stripHandToRanks(normalizedHand) : normalizedHand;
};

export const normalizePostflopSearchInput = (raw = '') => raw
  .split('')
  .filter((char) => (
    VALID_RANKS.includes(char.toUpperCase()) || VALID_SEARCH_SUITS.includes(char.toLowerCase())
  ))
  .map((char) => {
    if (VALID_RANKS.includes(char.toUpperCase())) {
      return char.toUpperCase();
    }

    return char.toLowerCase();
  })
  .join('');

export const hasDuplicateSuitedCards = (hand = '') => {
  if (!handHasSuitNotation(hand)) return false;

  const cards = splitPostflopHand(hand).filter((card) => card[1] !== 'x');
  return cards.length !== new Set(cards).size;
};

export const isSpecificSuitHand = (hand = '') => {
  if (hand.length < 2) return false;

  const pairs = hand.match(/.{2}/g) || [];
  const validSuitedPairs = pairs.filter((pair) => {
    const rank = pair[0].toUpperCase();
    const suit = pair[1];

    return VALID_RANKS.includes(rank) && VALID_REAL_SUITS.includes(suit);
  });

  return validSuitedPairs.length >= 1
    && validSuitedPairs.length <= 3
    && (validSuitedPairs.length * 2 === hand.length);
};

export const isValidPostflopSearchHand = (
  hand = '',
  isPlo5 = false,
  selectedBoard = '',
) => {
  const maxCards = getPostflopMaxCards(isPlo5);

  if (handHasSuitNotation(hand)) {
    const cards = splitPostflopHand(hand);
    const cardsAreValid = cards.length === maxCards && cards.every((card) => (
      card.length === 2
      && VALID_RANKS.includes(card[0].toUpperCase())
      && VALID_SEARCH_SUITS.includes(card[1].toLowerCase())
    ));

    if (!cardsAreValid || hasDuplicateSuitedCards(hand)) return false;

    const boardCards = new Set(splitPostflopHand(selectedBoard));
    if (cards.some((card) => card[1] !== 'x' && boardCards.has(card))) return false;

    const rankCounts = cards.reduce((counts, card) => ({
      ...counts,
      [card[0]]: (counts[card[0]] || 0) + 1,
    }), {});
    return Object.values(rankCounts).every((count) => count <= 4);
  }

  if (hand.length !== maxCards) return false;
  if (!hand.split('').every((rank) => VALID_RANKS.includes(rank.toUpperCase()))) return false;

  const rankCounts = hand.split('').reduce((counts, rank) => ({
    ...counts,
    [rank]: (counts[rank] || 0) + 1,
  }), {});
  return Object.values(rankCounts).every((count) => count <= 4);
};

export const shouldUseNonSuitSelection = (cards = []) => {
  if (!cards.length) return false;

  if (cards.some((card) => card.includes('x'))) {
    return true;
  }

  if (cards.some((card) => card[1] && VALID_SEARCH_SUITS.includes(card[1]?.toLowerCase()))) {
    return false;
  }

  return true;
};

export const isPostflopHandTypePossible = (handType = '', board = '') => {
  if (!handType || !board) return true;

  const cards = splitPostflopHand(board);
  if (cards.length !== 3 || cards.some((card) => card.length !== 2)) return true;

  const ranks = cards.map((card) => card[0].toUpperCase());
  const uniqueRanks = [...new Set(ranks)];
  const suits = cards.map((card) => card[1].toLowerCase());
  const suitCounts = suits.reduce((counts, suit) => ({
    ...counts,
    [suit]: (counts[suit] || 0) + 1,
  }), {});
  const maxSuitCount = Math.max(...Object.values(suitCounts));
  const boardHasStraight = uniqueRanks.length === 3
    && STRAIGHT_RANKS.some((straight) => uniqueRanks.every((rank) => straight.includes(rank)));
  const boardAllowsStraightDraw = STRAIGHT_RANKS.some((straight) => (
    uniqueRanks.filter((rank) => straight.includes(rank)).length >= 2
  ));
  const maxRankCount = Math.max(...uniqueRanks.map((rank) => (
    ranks.filter((boardRank) => boardRank === rank).length
  )));

  switch (handType) {
    case 'one_pair':
    case 'two_pair':
      return maxRankCount < 3;
    case 'straight':
      return boardHasStraight;
    case 'flush':
      return maxSuitCount === 3;
    case 'sd':
      return boardAllowsStraightDraw;
    case 'fd':
      return maxSuitCount === 2;
    case 'bdfd':
      return maxSuitCount < 3;
    default:
      return true;
  }
};

export const filterPostflopHandTypeOptions = (options = [], board = '') => (
  options.filter((option) => isPostflopHandTypePossible(option.value, board))
);
